import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { DayPicker } from "react-day-picker";
import { format, parseISO } from "date-fns";
import { CalendarDays, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import "react-day-picker/style.css";

export interface DatePickerProps {
  /** ISO date (`yyyy-MM-dd`), or null when nothing is chosen. */
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /**
   * Heading inside the calendar modal. Defaults to "Due date" — the original
   * and still the common caller. A field that calls itself something else must
   * pass its own, or the calendar contradicts the trigger that opened it.
   */
  heading?: string;
  /** Label on the action that empties the field. Follows {@link heading}. */
  clearLabel?: string;
  className?: string;
  id?: string;
  "aria-label"?: string;
}

/**
 * Single-date picker with a field-style trigger.
 *
 * A sibling to {@link DateRangePicker} rather than an option on it: the range
 * picker's whole body is two-tap partial-selection state that a single date
 * has no use for, and folding both into one component would mean a mode flag
 * threaded through every branch of it.
 *
 * The trigger shows a formatted date (`15 Aug 2026`) rather than a native
 * `dd/mm/yyyy` input, because the field is read far more often than it is
 * edited and a formatted date is what the design specifies.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "No due date",
  heading = "Due date",
  clearLabel = "Clear due date",
  className,
  id,
  "aria-label": ariaLabel,
}: DatePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Date-only values are parsed as local midnight, never as an instant — an
  // ISO date treated as UTC lands on the previous day for viewers behind it.
  const selected = value ? parseISO(value) : undefined;

  const calendarStyles = createPortal(
    <style>{`
      .otus-date-picker .rdp-root {
        --rdp-accent-color: var(--primary);
        --rdp-accent-background-color: color-mix(in oklab, var(--primary) 18%, transparent);
        font-family: var(--font-sans, 'DM Sans', sans-serif);
      }
      .otus-date-picker .rdp-day_button:hover:not(:disabled) {
        background-color: color-mix(in oklab, var(--primary) 25%, transparent);
        color: var(--foreground);
      }
      .otus-date-picker .rdp-today:not(.rdp-selected) .rdp-day_button {
        background-color: var(--accent);
        color: var(--foreground);
        font-weight: 600;
      }
      .otus-date-picker .rdp-chevron { fill: var(--muted-foreground); }
    `}</style>,
    document.head,
  );

  return (
    <>
      <button
        type="button"
        id={id}
        aria-label={ariaLabel}
        onClick={() => setIsOpen(true)}
        className={cn(
          "flex h-[46px] w-full items-center justify-between gap-2.5 rounded-lg border border-border bg-card px-3 text-sm",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-2.5">
          <CalendarDays
            className="h-4 w-4 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {selected ? format(selected, "d MMM yyyy") : placeholder}
          </span>
        </span>
        <ChevronDown
          className="h-4 w-4 shrink-0 text-muted-foreground"
          aria-hidden="true"
        />
      </button>

      {isOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            // Explicit, and load-bearing. This portals into `document.body`,
            // which sits OUTSIDE the drawer that usually hosts the trigger —
            // and vaul sets `pointer-events: none` on the body while a drawer
            // is open. `pointer-events` inherits, so without this the calendar
            // renders perfectly and ignores every click.
            style={{ pointerEvents: "auto" }}
            onClick={() => setIsOpen(false)}
          >
            <div
              className="otus-date-picker rounded-xl border border-border bg-card p-4 shadow-lg"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium">{heading}</span>
                <button
                  type="button"
                  aria-label="Close"
                  onClick={() => setIsOpen(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <DayPicker
                mode="single"
                weekStartsOn={1}
                selected={selected}
                // Open on the month of the date already chosen, not on today's.
                // Without this the calendar opens on the current month and a
                // value set for a later one is not even on screen — you have to
                // page forward to find your own selection. The modal mounts
                // fresh on each open, so this is re-read every time.
                defaultMonth={selected}
                onSelect={(day) => {
                  onChange(day ? format(day, "yyyy-MM-dd") : null);
                  setIsOpen(false);
                }}
                className="m-0"
                fixedWeeks
              />
              {value && (
                <button
                  type="button"
                  onClick={() => {
                    onChange(null);
                    setIsOpen(false);
                  }}
                  className="mt-2 w-full rounded-md border border-border py-2 text-sm font-medium"
                >
                  {clearLabel}
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
      {calendarStyles}
    </>
  );
}
